import { useOutletContext } from 'react-router-dom';

import {
  useLatestBackfill,
  useLatestOnboarding,
  useRepositories,
  useResetBackfill,
  useStartBackfill,
  useStartOnboarding,
  type Backfill,
  type OnboardingSummary,
  type Repository,
} from '../api.ts';
import { LinkableRepos } from '../components/LinkableRepos.tsx';

import type { SettingsOutletContext } from './Settings.tsx';

/**
 * Repositories section of /settings/data-sources.
 *
 * Two sections:
 *   1. **Linked repos** — every row in the new-schema `repositories`
 *      table for this org, regardless of ingest source. Each row has
 *      a backfill status pill and a "Backfill" / "Re-backfill" button.
 *      `useLatestBackfill` polls per row at 20s while a run is
 *      `running` and stops on terminal status.
 *   2. **Available via GitHub App** — for each connected installation,
 *      list repos the install can see; show "Link" for unlinked rows
 *      (creates the new-schema row + immediately kicks off a backfill),
 *      hide rows that are already linked (they appear in section 1).
 *
 * A repo is a repo: GitHub-App-linked or not. The page treats them
 * the same. Other ingest sources (local skill, MCP, manual) surface
 * naturally as rows in section 1.
 */
export function SettingsRepositories() {
  const { orgSlug } = useOutletContext<SettingsOutletContext>();
  const repositories = useRepositories(orgSlug);

  if (repositories.isPending) {
    return <p className="muted">Loading…</p>;
  }
  if (repositories.isError) {
    return <p style={{ color: '#c0392b' }}>Error: {repositories.error.message}</p>;
  }
  return (
    <>
      <h2>Repositories</h2>
      <p className="muted">
        Repositories this org has ingested or can ingest. A repo can be linked to a GitHub App
        install (for auto-sync via webhook) or added via local skill, MCP, or manual upload —
        the canonical entity here is the repo itself.
      </p>

      <LinkedReposSection orgSlug={orgSlug} repos={repositories.data} />

      <h3 style={{ marginTop: '2rem' }}>Available via GitHub App</h3>
      <LinkableRepos orgSlug={orgSlug} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Section 1 — linked repos with backfill action
// ---------------------------------------------------------------------------

function LinkedReposSection({
  orgSlug,
  repos,
}: {
  orgSlug: string;
  repos: Repository[];
}) {
  if (repos.length === 0) {
    return (
      <p className="muted">
        No repositories linked yet. Link one from the &ldquo;Available via GitHub App&rdquo;
        list below.
      </p>
    );
  }
  return (
    <ul className="bare stack" style={{ marginTop: '0.5rem' }}>
      {repos.map((r) => (
        <li key={r.id}>
          <LinkedRepoRow orgSlug={orgSlug} repo={r} />
        </li>
      ))}
    </ul>
  );
}

function LinkedRepoRow({ orgSlug, repo }: { orgSlug: string; repo: Repository }) {
  const backfill = useLatestBackfill(orgSlug, repo.id);
  const startBackfill = useStartBackfill(orgSlug, repo.id);
  const resetBackfill = useResetBackfill(orgSlug, repo.id);
  const onboarding = useLatestOnboarding(orgSlug, repo.id);
  const startOnboarding = useStartOnboarding(orgSlug, repo.id);
  const isBackfillRunning = backfill.data?.status === 'running';
  const isOnboardingRunning =
    onboarding.data?.status === 'running' || onboarding.data?.status === 'queued';
  const backfillButtonLabel = !backfill.data
    ? 'Backfill'
    : isBackfillRunning
      ? 'Backfill running…'
      : 'Re-backfill';
  // Full onboarding (re-derives BFs + derivedContexts + skills + clusters).
  const onboardingButtonLabel = isOnboardingRunning ? 'Onboarding…' : 'Re-onboard';
  const onClickOnboarding = () => {
    // Confirm before any re-run — wipes + rewrites the catalog and
    // costs real LLM $.
    if (onboarding.data && onboarding.data.status !== 'failed') {
      const ok = confirm(
        'Re-onboarding will wipe and rewrite the catalog (business functions, contexts, skills, file clusters). It takes 5-15 minutes and costs LLM tokens. Continue?',
      );
      if (!ok) return;
    }
    startOnboarding.mutate({});
  };
  // Re-skill: derivedContexts + skills only, keeping the current business
  // functions (pinned server-side). One LLM pass, no BF re-draft or
  // engineer/observation rebuild — the fast iteration loop.
  const onClickReskill = () => {
    if (isOnboardingRunning) return;
    const ok = confirm(
      'Re-skill re-derives contexts + skills only, keeping the current business functions. It clones the repo and runs one LLM pass (~2–5 min, costs tokens). Continue?',
    );
    if (!ok) return;
    startOnboarding.mutate({ steps: ['signals', 'derivedContexts'] });
  };
  const onClickForceReset = () => {
    const ok = confirm(
      `Force reset will delete ALL commit history, file paths, and ingest runs for ${repo.owner}/${repo.name} and start a fresh backfill from zero. This cannot be undone. Continue?`,
    );
    if (!ok) return;
    resetBackfill.mutate();
  };
  return (
    <div
      className="stack"
      style={{
        border: '1px solid rgba(127,127,127,0.2)',
        borderRadius: '6px',
        padding: '0.75rem 1rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <code>{repo.owner}/{repo.name}</code>
          <span className="muted"> · {repo.default_branch}</span>
          <span className="muted"> · {sourceLabel(repo)}</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn"
            disabled={startBackfill.isPending || isBackfillRunning}
            // Re-backfill = force-skip the HEAD-sha pre-check (otherwise
            // we'd get back `noop` for an unchanged HEAD). Initial
            // backfill doesn't need force.
            onClick={() =>
              startBackfill.mutate({ force: !!backfill.data })
            }
          >
            {backfillButtonLabel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={startOnboarding.isPending || isOnboardingRunning}
            onClick={onClickReskill}
          >
            Re-skill
          </button>
          <button
            type="button"
            className="btn"
            disabled={startOnboarding.isPending || isOnboardingRunning}
            onClick={onClickOnboarding}
          >
            {onboardingButtonLabel}
          </button>
        </div>
      </div>
      <BackfillRowStatus backfill={backfill.data ?? null} />
      <OnboardingRowStatus onboarding={onboarding.data ?? null} />
      <div style={{ fontSize: '0.85em' }}>
        <button
          type="button"
          // Plain link-style button: destructive enough to want explicit
          // styling that it's not a primary action, but not so loud it
          // looks like a trap. Confirm dialog is the real guard.
          className="link-button"
          style={{
            color: '#c0392b',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          disabled={resetBackfill.isPending || isBackfillRunning}
          onClick={onClickForceReset}
        >
          {resetBackfill.isPending ? 'Force reset running…' : 'Force reset'}
        </button>
      </div>
      {startBackfill.isError ? (
        <p style={{ color: '#c0392b' }}>{startBackfill.error.message}</p>
      ) : null}
      {startOnboarding.isError ? (
        <p style={{ color: '#c0392b' }}>{startOnboarding.error.message}</p>
      ) : null}
      {resetBackfill.isError ? (
        <p style={{ color: '#c0392b' }}>{resetBackfill.error.message}</p>
      ) : null}
    </div>
  );
}

// Status-pill background colors. Match across BackfillRowStatus and
// OnboardingRowStatus so a glance at the row tells you which is
// where without re-parsing the label.
const STATUS_PILL_BG: Record<string, string | undefined> = {
  succeeded: '#d5f5e3',
  failed: '#fadbd8',
  running: undefined, // default pill bg
  queued: undefined,
};

const ONBOARDING_STEPS: ReadonlyArray<OnboardingSummary['current_step']> = [
  'signals',
  'bfs',
  'derivedContexts',
  'engineers',
  'observations',
];
const ONBOARDING_STEP_LABELS: Record<string, string> = {
  signals: 'reading the repo',
  bfs: 'defining business functions',
  derivedContexts: 'defining contexts, skills, and clusters',
  engineers: 'deriving the engineer roster',
  observations: 'computing engineer ratings',
};
const ONBOARDING_STEP_TOTAL = ONBOARDING_STEPS.length;

function OnboardingRowStatus({ onboarding }: { onboarding: OnboardingSummary | null }) {
  if (!onboarding) {
    return <p className="muted" style={{ fontSize: '0.9em' }}>Onboarding: <strong>no runs yet</strong></p>;
  }
  const { status, current_step } = onboarding;
  const stepIdx = current_step ? ONBOARDING_STEPS.indexOf(current_step) : -1;
  const friendlyStep = current_step ? ONBOARDING_STEP_LABELS[current_step] ?? current_step : '';
  const stepLabel =
    status === 'running' && current_step && stepIdx >= 0
      ? `step ${stepIdx + 1} of ${ONBOARDING_STEP_TOTAL} — ${friendlyStep}`
      : '';
  return (
    <div style={{ fontSize: '0.9em' }}>
      <span className="muted">Onboarding:</span>{' '}
      <span className="pill" style={{ background: STATUS_PILL_BG[status] }}>{status}</span>
      {stepLabel ? <span className="muted"> · {stepLabel}</span> : null}
      {status === 'succeeded' && onboarding.completed_at ? (
        <span className="muted"> · {onboarding.completed_at}</span>
      ) : null}
      {status === 'failed' && onboarding.error_message ? (
        <span className="muted"> · {onboarding.error_message}</span>
      ) : null}
    </div>
  );
}

function BackfillRowStatus({ backfill }: { backfill: Backfill | null }) {
  if (!backfill) {
    return <p className="muted" style={{ fontSize: '0.9em' }}>Backfill: <strong>no runs yet</strong></p>;
  }
  const { status, progress } = backfill;
  if (status === 'running') {
    const total = progress?.total_commits ?? null;
    const so_far = progress?.commits_so_far ?? null;
    const pct = progress?.percent;
    return (
      <div style={{ fontSize: '0.9em' }}>
        <span className="muted">Backfill:</span>{' '}
        <span className="pill">running</span>
        <span className="muted"> · stage: {progress?.stage ?? 'starting'}</span>
        {total != null ? (
          <span className="muted">
            {' '}
            · {so_far?.toLocaleString() ?? 0} / {total.toLocaleString()} commits
            {pct != null ? ` (${pct}%)` : null}
          </span>
        ) : null}
        <ProgressBar percent={pct ?? null} />
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div style={{ fontSize: '0.9em' }}>
        <span className="muted">Backfill:</span>{' '}
        <span className="pill" style={{ background: STATUS_PILL_BG.failed }}>failed</span>
        {backfill.error ? <span className="muted"> · {backfill.error}</span> : null}
      </div>
    );
  }
  if (status === 'succeeded') {
    return (
      <div style={{ fontSize: '0.9em' }}>
        <span className="muted">Backfill:</span>{' '}
        <span className="pill" style={{ background: STATUS_PILL_BG.succeeded }}>succeeded</span>
        <span className="muted"> · {backfill.completed_at ?? '—'}</span>
      </div>
    );
  }
  return (
    <div style={{ fontSize: '0.9em' }}>
      <span className="muted">Backfill:</span>{' '}
      <span className="pill">{status}</span>
    </div>
  );
}

function ProgressBar({ percent }: { percent: number | null }) {
  const pct = percent ?? 0;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent ?? undefined}
      style={{
        background: 'rgba(127,127,127,0.15)',
        height: '6px',
        borderRadius: '3px',
        marginTop: '0.35rem',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: '#2ecc71',
          height: '100%',
          width: `${Math.max(2, pct)}%`,
          transition: 'width 0.3s ease-out',
        }}
      />
    </div>
  );
}

function sourceLabel(repo: Repository): string {
  const sources: string[] = [];
  if (repo.github_installation_id) sources.push('GitHub App');
  switch (repo.ingest_mode) {
    case 'local_skill':
      sources.push('local skill');
      break;
    case 'cloud_artifacts':
      sources.push('cloud artifacts');
      break;
    case 'rest_only':
      // already covered by "GitHub App" if installation_id present;
      // emit only when installation_id is absent (rare).
      if (!repo.github_installation_id) sources.push('REST');
      break;
    case 'manual':
      sources.push('manual');
      break;
  }
  return sources.length ? sources.join(' + ') : 'unknown';
}

